import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Grid, Form, DateInput, Radio, Tooltip } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';

import { SectionHeader, Section } from '@/components/Common';
import { AssistantContext } from '@/contexts';
import { useTypesVoies } from '@/hooks';
import {
  fromAdresseFormReferentiel,
  paysTypeAdresse,
  toAssistantAdresseFormValue,
} from '@/utils';
import {
  AIDE_MATERIELLE_FIXE,
  COLORS,
  OPTIONS_TYPE_CONVENTION,
  OPTIONS_AIDE_MATERIELLE_ASSISTANT,
  OPTIONS_REGIME_BIEN_ASSISTANT,
  VALEUR_AIDE_MAX_LENGTH,
} from '@/constants';

interface InformationsConventionProps {
  typeConvention: number | null;
  dateConvention: string;
  adresseConvention: any;
  aideMaterielle?: number | null;
  valeurAide?: number | null;
  regimeBien?: number | null;
  errors: Record<string, string>;
}

const InformationsConvention = (props: InformationsConventionProps) => {
  const {
    typeConvention,
    dateConvention,
    adresseConvention,
    aideMaterielle,
    valeurAide,
    regimeBien,
    errors,
  } = props;

  const { selectedDeclarationType, onChange } =
    React.useContext(AssistantContext);

  // Projet : les dates de signature sont facultatives (le projet peut être
  // créé sans), elles deviennent obligatoires à la déclaration initiale.
  const isProjet = selectedDeclarationType === 'projet';
  const typesVoies = useTypesVoies();
  const [selectedConvention, setSelectedConvention] = React.useState<
    number | null
  >(typeConvention ?? null);

  const [dateConventionObj, setDateConventionObj] = React.useState<Date | null>(
    dateConvention ? new Date(dateConvention) : null,
  );

  React.useEffect(() => {
    setDateConventionObj(dateConvention ? new Date(dateConvention) : null);
  }, [dateConvention]);

  React.useEffect(() => {
    setSelectedConvention(typeConvention ?? null);
  }, [typeConvention]);

  const formatConventionAdresse = React.useCallback(
    (baseAdresse: any, adresse: any) => ({
      ...baseAdresse,
      ...(!_.isNil(adresse?.type) ? { type: adresse.type } : {}),
      ...(!_.isNil(adresse?.codePostal)
        ? { codePostal: adresse.codePostal }
        : {}),
      ...(!_.isNil(adresse?.commune) ? { localite: adresse.commune } : {}),
      ...(!_.isNil(adresse?.voie) ? { libelleVoie: adresse.voie } : {}),
      ...(!_.isNil(adresse?.pays) ? { libellePays: adresse.pays } : {}),
      ...fromAdresseFormReferentiel(adresse),
      ...(!_.isNil(adresse?.numeroVoirie)
        ? { numeroVoie: adresse.numeroVoirie }
        : {}),
      ...(!_.isNil(adresse?.complementNumeroVoirie)
        ? { complementNumeroVoie: adresse.complementNumeroVoirie }
        : {}),
      ...(!_.isNil(adresse?.complementGeographique)
        ? { complementGeographique: adresse.complementGeographique }
        : {}),
      ...(!_.isNil(adresse?.complementDistribution)
        ? { complementDistribution: adresse.complementDistribution }
        : {}),
      ...(!_.isNil(adresse?.complementIdentification)
        ? { complementIdentification: adresse.complementIdentification }
        : {}),
    }),
    [],
  );

  const handleAdresseItemChange = React.useCallback(
    (field: string, baseAdresse: any) =>
      ({ adresse }) => {
        // AdresseForm émet parfois une adresse vide (mises à jour groupées) :
        // l'appliquer remettrait l'adresse d'origine par-dessus la saisie.
        if (_.isEmpty(adresse)) return;

        onChange(null, {
          name: `conventionDeclaration.${field}`,
          value: formatConventionAdresse(baseAdresse, { ...adresse }),
        });
      },
    [formatConventionAdresse, onChange],
  );

  const handleAdresseChange = React.useCallback(
    (field: string, baseAdresse: any) =>
      ({ target: { name, value } }) => {
        // Seul le type est écrit : remplacer l'adresse par `{ type }` effaçait
        // celle que `onItemChange` vient de réinitialiser (commune déléguée…).
        if (name === 'adresse.type') {
          onChange(null, {
            name: `conventionDeclaration.${field}.type`,
            value,
          });
          // La réinitialisation d'AdresseForm conserve le type de voie de
          // l'adresse précédente.
          onChange(null, {
            name: `conventionDeclaration.${field}.typeVoie`,
            value: '',
          });
          const pays = paysTypeAdresse(value);
          if (pays) {
            onChange(null, {
              name: `conventionDeclaration.${field}.libellePays`,
              value: pays,
            });
          }

          return;
        }

        const adresseField = _.replace(name, 'adresse.', '');

        onChange(null, {
          name: `conventionDeclaration.${field}`,
          value: formatConventionAdresse(baseAdresse, {
            [adresseField]: value,
          }),
        });
      },
    [formatConventionAdresse, onChange],
  );

  const handleChange = React.useCallback(
    (field: string, value: string | boolean) => {
      onChange(null, {
        name: `conventionDeclaration.adresseConvention.${field}`,
        value,
      });
    },
    [onChange],
  );

  const handleTypeConventionChange = React.useCallback(
    (value: number | null) => {
      setSelectedConvention(value);
      onChange(null, {
        name: 'conventionDeclaration.typeConvention',
        value,
      });
    },
    [onChange],
  );

  const handleAideMaterielleChange = React.useCallback(
    (value: number) => {
      onChange(null, {
        name: 'conventionDeclaration.aideMaterielle',
        value,
      });

      // Le montant n'a de sens que pour le cas « Fixée à la hauteur de »
      if (value !== AIDE_MATERIELLE_FIXE) {
        onChange(null, {
          name: 'conventionDeclaration.valeurAide',
          value: null,
        });
      }
    },
    [onChange],
  );

  const handleValeurAideChange = React.useCallback(
    (value: string) => {
      // `maxLength` est ignoré par les input de type number
      if (value.length > VALEUR_AIDE_MAX_LENGTH) return;

      onChange(null, {
        name: 'conventionDeclaration.valeurAide',
        value: value === '' ? null : Number(value),
      });
    },
    [onChange],
  );

  const handleRegimeBienChange = React.useCallback(
    (value: number) => {
      onChange(null, {
        name: 'conventionDeclaration.regimeBien',
        value,
      });
    },
    [onChange],
  );

  const handleDateConventionChange = React.useCallback(
    (value: string | Date | null) => {
      const rawValue = value;

      if (_.isNil(rawValue) || rawValue === '') {
        setDateConventionObj(null);
        onChange(null, {
          name: 'conventionDeclaration.dateConvention',
          value: null,
        });
        return;
      }

      const parsedDate =
        rawValue instanceof Date ? rawValue : new Date(rawValue);
      const nextDate = Number.isNaN(parsedDate.getTime()) ? null : parsedDate;

      setDateConventionObj(nextDate);
      onChange(null, {
        name: 'conventionDeclaration.dateConvention',
        value: nextDate ? dayjs(nextDate).format('YYYY-MM-DD') : null,
      });
    },
    [onChange],
  );

  return (
    <Section
      data-testid="assistant-convention"
      title={`CONVENTION ${_.upperCase(selectedDeclarationType)}`}
    >
      {/* @ts-ignore */}
      <Form style={{ display: 'flex' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: 14,
          }}
        >
          <SectionHeader title="Choix de convention" />
          <Grid padded>
            <Grid.Row>
              <Grid.Column width={16}>
                {_.map(OPTIONS_TYPE_CONVENTION, (optionTypeConvention) => (
                  <Form.Radio
                    key={optionTypeConvention.key}
                    name="choixConvention"
                    data-testid={`assistant-convention-type-${optionTypeConvention.key}`}
                    label={optionTypeConvention.text}
                    value={optionTypeConvention.key}
                    checked={
                      selectedConvention === Number(optionTypeConvention.key)
                    }
                    onChange={(_e, data) =>
                      handleTypeConventionChange(
                        _.isNil(data.value) ? null : Number(data.value),
                      )
                    }
                  />
                ))}
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <SectionHeader title="Aide matérielle" />
          <Grid padded>
            <Grid.Row>
              <Grid.Column width={16}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75em',
                  }}
                >
                  {_.map(OPTIONS_AIDE_MATERIELLE_ASSISTANT, (optionAide) => (
                    <div
                      key={optionAide.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5em',
                      }}
                    >
                      <Radio
                        name="aideMaterielle"
                        data-testid={`assistant-convention-aide-materielle-${optionAide.key}`}
                        label={optionAide.text}
                        value={optionAide.key}
                        checked={aideMaterielle === optionAide.key}
                        onChange={() =>
                          handleAideMaterielleChange(optionAide.key)
                        }
                      />
                      {optionAide.key === AIDE_MATERIELLE_FIXE && (
                        <>
                          <div
                            className={
                              aideMaterielle !== AIDE_MATERIELLE_FIXE
                                ? 'ui disabled input'
                                : 'ui input'
                            }
                            style={{ maxWidth: 140 }}
                          >
                            <input
                              type="number"
                              data-testid="assistant-convention-valeur-aide"
                              disabled={aideMaterielle !== AIDE_MATERIELLE_FIXE}
                              value={valeurAide ?? ''}
                              onChange={(e) =>
                                handleValeurAideChange(e.target.value)
                              }
                            />
                          </div>
                          <label style={{ whiteSpace: 'nowrap' }}>
                            euros par an
                          </label>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <SectionHeader title="Régime des biens" />
          <Grid padded>
            <Grid.Row>
              <Grid.Column width={16}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1em',
                  }}
                >
                  {_.map(OPTIONS_REGIME_BIEN_ASSISTANT, (optionRegime) => (
                    <Radio
                      key={optionRegime.key}
                      name="regimeBien"
                      data-testid={`assistant-convention-regime-bien-${optionRegime.key}`}
                      label={optionRegime.text}
                      value={optionRegime.key}
                      checked={regimeBien === optionRegime.key}
                      onChange={() => handleRegimeBienChange(optionRegime.key)}
                      // Évite un espacement trop petit lorsqu'une option prend plus d'une ligne
                      style={{ lineHeight: '1.5em' }}
                    />
                  ))}
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
        <div
          style={{
            border: `1px solid ${COLORS.DividerColor}`,
            margin: '0px 2rem',
            width: 1,
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: 14,
          }}
        >
          <SectionHeader title="Signature des partenaires" />
          <Grid padded>
            <Grid.Row style={{ paddingBottom: 0 }}>
              <Grid.Column
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: 0,
                  paddingBottom: 0,
                  width: 'auto',
                  minWidth: 'fit-content',
                }}
              >
                {/* @ts-ignore */}
                <Form.Field required={!isProjet} style={{ margin: 0 }}>
                  <label style={{ margin: 0, whiteSpace: 'nowrap' }}>
                    Signée le
                  </label>
                </Form.Field>
              </Grid.Column>
              <Grid.Column
                style={{
                  display: 'flex',
                  paddingTop: 0,
                  paddingBottom: 0,
                  width: '18%',
                }}
              >
                <Tooltip
                  trigger={
                    <div>
                      <DateInput
                        data-testid="assistant-convention-date-signature"
                        value={dateConventionObj}
                        onChange={handleDateConventionChange}
                        error={!!errors?.dateConventionInitiale}
                      />
                    </div>
                  }
                  type={errors?.dateConventionInitiale ? 'error' : ''}
                  message={errors?.dateConventionInitiale}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <SectionHeader
            title="Lieu de signature"
            style={{ marginBottom: 0 }}
          />
          <Grid padded>
            <Grid.Row>
              <Grid.Column width={16}>
                <AdresseForm
                  saisie="libre"
                  handleTypes
                  displayDepartement={false}
                  displayArticle
                  adresse={toAssistantAdresseFormValue(
                    adresseConvention,
                    typesVoies,
                  )}
                  onItemChange={handleAdresseItemChange(
                    'adresseConvention',
                    adresseConvention,
                  )}
                  onChange={handleAdresseChange(
                    'adresseConvention',
                    adresseConvention,
                  )}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <Grid padded>
            <Grid.Row verticalAlign="middle" style={{ padding: 0 }}>
              <Grid.Column width={6}>
                <label>&nbsp;</label>
                <Form.Checkbox
                  label="Commune déléguée"
                  data-testid="assistant-convention-signature-commune-delegue"
                  checked={adresseConvention?.communeDelegue}
                  onChange={(_e, data) => {
                    handleChange('communeDelegue', data.checked ?? false);

                    if (!data.checked) {
                      handleChange('libelleCommuneDelegue', '');
                    }
                  }}
                />
              </Grid.Column>
              <Grid.Column width={10}>
                <Tooltip
                  trigger={
                    <div>
                      <Form.Input
                        label="&nbsp;"
                        input={{
                          'data-testid':
                            'assistant-convention-signature-libelle-commune-delegue',
                        }}
                        value={adresseConvention?.libelleCommuneDelegue ?? ''}
                        disabled={!adresseConvention?.communeDelegue}
                        error={!!errors?.libelleCommuneDelegueConvention}
                        onChange={(_e, data) =>
                          handleChange(
                            'libelleCommuneDelegue',
                            data.value ?? '',
                          )
                        }
                      />
                    </div>
                  }
                  type={errors?.libelleCommuneDelegueConvention ? 'error' : ''}
                  message={errors?.libelleCommuneDelegueConvention}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Form>
    </Section>
  );
};

export default InformationsConvention;
